import React, { useEffect, useState } from 'react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import Title from '@components/ui/type/Title'
import Translations from '@helpers/translations'
import { submitOutOfStockForm } from '@api/apiCalls'
import { ProductOption, ProductVariant } from '../../../types/product'
import { analytics } from '@helpers/analytics'
import { SubmitButtonState } from '@components/ui/Button/SubmitButton'
import { Button, SubmitButton } from '@components/ui/Button'
import trackError from '@helpers/trackError'
import Notification from '@components/ui/Notification'
import styles from './styles.module.scss'
import { SelectedOptions, AvailableOptions } from './types'
import { findVariantForOptions } from '@helpers/filters'
import { FormProvider, useForm } from 'react-hook-form'
import { InputField } from '@components/ui/formElements/form/InputField'
import FormGrid from '@components/ui/formElements/form/shared/FormGrid'
import { SelectField } from '@components/ui/formElements/form/selectFields/SelectField'

export const BackInStockSchema = z
  .object({
    email: z.string().trim().email('Enter a valid email address')
  })
  .catchall(
    z
      .string()
      .min(1, 'Please select an option')
      .refine(val => val !== '', 'Please select an option')
  )

export type BackInStockFormValues = z.infer<typeof BackInStockSchema>

const sendAnalyticsEvents = (
  options: SelectedOptions,
  productType: string,
  productTitle: string
) => {
  analytics('Out of Stock - Completed', {
    label: 'completed',
    name: `${productType} - ${productTitle}`,
    props: options
  })
}

type SelectedProductProperties = {
  id: number
  title: string
  productType: string
  productFeed: {
    product_id?: string
  }
  options: ProductOption[]
  variants?: ProductVariant[]
}

type BackInStockFormWrapperProps = {
  availableOptions: AvailableOptions
  selectedOptions: SelectedOptions
  selectedProduct: SelectedProductProperties
  handleCloseModal: () => void
  setSelectedOptions: React.Dispatch<React.SetStateAction<SelectedOptions>>
}

const FormNotification = ({ state }: { state: string }) => {
  switch (state) {
    case 'error':
      return (
        <Notification
          type="error"
          classes={styles.modalFormWrapper__Form__Notification}
          content={Translations.localise('product.oos-modal.error')}
        />
      )
    case 'success':
      return (
        <Notification
          type="success"
          classes={styles.modalFormWrapper__Form__Notification}
          content={Translations.localise('product.oos-modal.success')}
        />
      )
    default:
      return null
  }
}

function formatOptions(options: SelectedOptions): SelectedOptions {
  const formattedOptions: SelectedOptions = {}
  Object.entries(options).forEach(([option, value]) => {
    const formattedOption = option
      .replace('taper', 'lowerLeg')
      .replace('swimshortsleg', 'shortsleg')
      .replace('chest size', 'size')
    formattedOptions[formattedOption] = value
  })
  return formattedOptions
}

const BackInStockFormWrapper = ({
  selectedOptions,
  availableOptions,
  selectedProduct,
  handleCloseModal,
  setSelectedOptions
}: BackInStockFormWrapperProps) => {
  const [buttonState, setButtonState] = useState<SubmitButtonState>('ready')
  const [showFormState, setShowFormState] = useState<'success' | 'error' | ''>(
    ''
  )

  const methods = useForm<BackInStockFormValues & { customError?: string }>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: zodResolver(BackInStockSchema),
    defaultValues: {
      email: ''
    }
  })
  const { productType, productFeed, id, title, variants } = selectedProduct

  const {
    formState: { isDirty, isValid },
    handleSubmit,
    setValue
  } = methods

  const isSubmitDisabled = !isDirty || !isValid || buttonState === 'loading'

  useEffect(() => {
    Object.entries(selectedOptions).forEach(([rawKey, rawValue]) => {
      const key = rawKey as keyof BackInStockFormValues
      const value = String(rawValue) // normalise to string

      setValue(key as string, value, {
        shouldValidate: false,
        shouldDirty: false
      })
    })
  }, [])

  useEffect(() => {
    const subscription = methods.watch(values => {
      const { email, ...optionValues } = values as BackInStockFormValues

      setSelectedOptions(current => ({
        ...current,
        ...optionValues
      }))
    })

    return () => subscription.unsubscribe()
  }, [methods, setSelectedOptions])

  const handleOnSubmit = async (values: BackInStockFormValues) => {
    setShowFormState('')
    methods.clearErrors('customError')

    const { email, ...optionValues } = values
    const formattedOptions = formatOptions(optionValues as SelectedOptions)
    const sku = findVariantForOptions(
      variants,
      optionValues as SelectedOptions
    )?.sku

    if (!sku) {
      trackError(new Error('Out of stock SKU could not be resolved'), {
        section: 'BackInStockModal',
        product_id: id,
        options: JSON.stringify(optionValues)
      })
    }

    const formPayload = {
      email,
      permanentDomain: /production|staging/.test(process.env.API_ENV || '')
        ? window.location.origin
        : '/',
      colour: title,
      productType,
      partial_sku: productFeed?.product_id || '',
      product_id: id,
      ...(sku && { sku }),
      ...formattedOptions
    }

    try {
      setButtonState('loading')
      await submitOutOfStockForm(formPayload)
      sendAnalyticsEvents(formattedOptions, productType, title)
      setShowFormState('success')
      setButtonState('ready')
    } catch (error) {
      trackError(new Error('Submit Out Of Stock Failed'))
      setShowFormState('error')
      setButtonState('ready')
    }
  }

  return (
    <div className={styles.modalFormWrapper__FormPanel}>
      <div className={styles.modalFormWrapper__FormPanel__Container}>
        <div className={styles.modalFormWrapper__FormPanel__Header}>
          <Title
            classes="title--secondary"
            type="h3"
            text="Select your preferred size"
          />
          <p>We will email you when it's back in stock.</p>
        </div>
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(handleOnSubmit)}
            noValidate
            className={styles.modalFormWrapper__Form}
          >
            <FormGrid>
              {availableOptions.map((item, index) => {
                const options = item.sizes.map(size => ({
                  value: size.value,
                  label: size.label,
                  available: true,
                  name: item.type
                }))
                return (
                  <div key={item.id} className={styles.ModalForm__field}>
                    <SelectField
                      id={item.type}
                      name={item.name}
                      placeholder={`Please select`}
                      options={options}
                      required={true}
                      settings={{ label: { visible: true } }}
                      forceDirection={
                        availableOptions.length - 1 == index ? 'up' : 'down'
                      }
                    />
                  </div>
                )
              })}
              <InputField
                type="email"
                name="email"
                label="Email"
                required={true}
                placeholder="Enter email address*"
              />
            </FormGrid>

            <FormNotification state={showFormState} />

            <div className={styles.modalFormWrapper__Form__Button}>
              {showFormState === 'success' ? (
                <Button
                  variant="primary"
                  fullWidth="always"
                  onClick={handleCloseModal}
                >
                  {Translations.localise('product.oos-modal.form.close-button')}
                </Button>
              ) : (
                <SubmitButton
                  fullWidth="always"
                  type="submit"
                  state={buttonState}
                  disabled={isSubmitDisabled}
                >
                  {Translations.localise('product.oos-modal.form.button')}
                </SubmitButton>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}

export default BackInStockFormWrapper
